import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { PortalType } from '@ats/api/cm';
import { ClientService } from '@cm/core/client';
import {
	BehaviorSubject,
	catchError,
	distinctUntilChanged,
	filter,
	map,
	Observable,
	of,
	shareReplay,
	switchMap,
	take,
} from 'rxjs';
import { ClientCultureAPIResponse, Culture, CULTURE_SERVICE_CONFIG, CultureServiceConfig } from '../models';

const DEFAULT_CULTURE: Culture = {
	code: 'en',
	label: 'English',
	nativeLabel: 'English',
};

@Injectable({
	providedIn: 'root',
})
export class CultureService {
	private readonly _culture = new BehaviorSubject<Culture | null>(null);
	readonly culture$: Observable<Culture> = this._culture.asObservable().pipe(filter(Boolean));
	readonly existingCultures$: Observable<Culture[]>;
	readonly supportedCultures$: Observable<Culture[]>;
	constructor(
		@Inject(CULTURE_SERVICE_CONFIG) private readonly config: CultureServiceConfig,
		private readonly http: HttpClient,
		private readonly clientService: ClientService
	) {
		this.existingCultures$ = this.fetchExistingCultures$().pipe(shareReplay(1));
		this.supportedCultures$ = this.fetchSupportedCultures$().pipe(shareReplay(1));
	}

	get cultureSnapshot(): string | null {
		return this._culture.getValue()?.code ?? null;
	}

	initialize$(cultureCode: string): Observable<boolean> {
		if (this.config.portal === PortalType.InternalPortal) {
			this.setCulture(DEFAULT_CULTURE);
			return of(true);
		}

		return this.fetchSupportedCultures$().pipe(
			catchError(() => of(false as false)),
			map((cultures: Culture[] | false) => {
				this.setCulture(
					(cultures || []).find(cltr => cltr.code === cultureCode) ||
						(cultures || []).find(cltr => cltr.code === 'en') ||
						DEFAULT_CULTURE
				);
				return !!cultures;
			}),
			take(1)
		);
	}

	private setCulture(culture: Culture) {
		this._culture.next(culture);
	}

	/* istanbul ignore next: private method */
	private fetchExistingCultures$(): Observable<Culture[]> {
		return this.clientService.client$.pipe(
			distinctUntilChanged(),
			switchMap(client =>
				this.http.get<ClientCultureAPIResponse>(`${this.config.api}/${client}/client-cultures`)
			),
			map(cultures =>
				Object.keys(cultures).map(code => ({
					code,
					label: cultures[code].label,
					nativeLabel: cultures[code].nativeLabel,
				}))
			)
		);
	}

	/* istanbul ignore next: private method */
	private fetchSupportedCultures$(): Observable<Culture[]> {
		return this.clientService.client$.pipe(
			distinctUntilChanged(),
			switchMap(client =>
				this.http.get<ClientCultureAPIResponse>(`${this.config.api}/${client}/supported-cultures`)
			),
			map(cultures =>
				Object.keys(cultures).map(code => ({
					code,
					label: cultures[code].label,
					nativeLabel: cultures[code].nativeLabel,
				}))
			)
		);
	}
}
